import { useEffect, useState } from "react";
import * as assegnabiliApi from "../../api/assegnabili";
import { UtenteAssegnabile } from "../../api/assegnabili";
import { useAuth } from "../../context/AuthContext";

const ETICHETTA_RUOLO: Record<string, string> = {
  admin: "Admin",
  staff: "Staff",
  centro_assistenza: "Centro Assistenza",
  operatore_tecnico: "Operatore tecnico",
  istituto_vigilanza: "Istituto di Vigilanza",
  operatore_vigilanza: "Operatore vigilanza",
  cliente: "Cliente",
};

function nomeDi(u: UtenteAssegnabile): string {
  const etichetta = ETICHETTA_RUOLO[u.role] ?? u.role;
  return `${u.full_name?.trim() || u.email} — ${etichetta}`;
}

/**
 * Il centro di chi sta compilando, se è un account `centro_assistenza`.
 *
 * Serve ai form di creazione in cui un CA non sceglie il centro: il campo resta
 * vuoto perché il backend al salvataggio lo forza al centro del chiamante, ma
 * `AssegnatarioSelect` senza un centro chiedeva gli assegnabili di nessuno e
 * riceveva 403 (issue #220). Passandogli questo valore il form dice quello che
 * il backend farà comunque. Non è un'autorizzazione: `/api/assegnabili`
 * risponde 403 a un CA che chiede un centro diverso dal suo.
 *
 * Non va usato dove il backend non lega il record al centro del chiamante —
 * le attivazioni collegate a un'installazione — né dove un CA non può
 * assegnare affatto, come i Sopralluoghi: il campo si attiverebbe e il
 * salvataggio scarterebbe o rifiuterebbe la scelta.
 */
export function useCentroDelChiamante(): string | null {
  const { user } = useAuth();
  return user?.role === "centro_assistenza" ? user.centro_assistenza_id : null;
}

/**
 * Campo "Assegnato a" condiviso dai quattro moduli operativi.
 *
 * L'elenco NON è statico: dipende da chi possiede il record, perché un
 * assegnatario deve appartenere a quell'organizzazione — assegnare a qualcuno
 * di fuori farebbe uscire il record dal suo scope e sparire dalla lista di
 * tutti quelli che potrebbero occuparsene. Il criterio vive nel backend
 * (`utils/assegnazione.ts`) e qui non è ricopiato: si chiede a lui chi è
 * ammesso, così i due non possono divergere.
 *
 * Cambiando azienda o centro nel form l'elenco si ricarica, e se
 * l'assegnatario scelto non è più fra gli ammessi viene azzerato: lasciarlo
 * lì produrrebbe un salvataggio rifiutato con un nome che sembrava valido.
 */
export function AssegnatarioSelect({
  value,
  onChange,
  cliente_id,
  centro_assistenza_id,
  istituto_vigilanza_id,
  sopralluogo_id,
  disabled,
  aiuto,
}: {
  value: string;
  onChange: (id: string) => void;
  cliente_id?: string | null;
  centro_assistenza_id?: string | null;
  istituto_vigilanza_id?: string | null;
  /**
   * Su un sopralluogo esistente: i centri con cui è CONDIVISO contano come
   * appartenenza, perché la loro gente il record lo vede già (lo scoping
   * include le condivisioni) e quindi può esserne responsabile. Si passa l'id
   * e non l'elenco dei centri: le condivisioni le risolve il backend dal
   * database, perché un elenco fornito dal client sarebbe un'affermazione di
   * privilegio non verificata.
   */
  sopralluogo_id?: string | null;
  disabled?: boolean;
  /** Testo sotto il campo, se il modulo ha qualcosa da spiegare. */
  aiuto?: string;
}) {
  const [utenti, setUtenti] = useState<UtenteAssegnabile[]>([]);
  const [stato, setStato] = useState<"carico" | "pronto" | "vietato">("carico");

  useEffect(() => {
    let annullato = false;
    setStato("carico");
    assegnabiliApi
      .listAssegnabili({ cliente_id, centro_assistenza_id, istituto_vigilanza_id, sopralluogo_id })
      .then(({ utenti: lista }) => {
        if (annullato) return;
        setUtenti(lista);
        setStato("pronto");
        // L'assegnatario corrente non è più ammesso (è cambiata l'azienda o il
        // centro): si azzera invece di restare selezionato e far fallire il
        // salvataggio.
        if (value && !lista.some((u) => u.id === value)) onChange("");
      })
      .catch(() => {
        if (annullato) return;
        // 403: chi guarda non può assegnare per questo proprietario. Il campo
        // si disabilita invece di mostrare un elenco vuoto, che si leggerebbe
        // come "non c'è nessuno disponibile".
        setUtenti([]);
        setStato("vietato");
      });
    return () => {
      annullato = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cliente_id, centro_assistenza_id, istituto_vigilanza_id, sopralluogo_id]);

  const bloccato = disabled || stato !== "pronto";

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-ink" htmlFor="assegnato-a">
        Assegnato a
      </label>
      <select
        id="assegnato-a"
        className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink disabled:bg-ink/[0.03] disabled:text-ink/40"
        value={value}
        disabled={bloccato}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">— Nessuno —</option>
        {utenti.map((u) => (
          <option key={u.id} value={u.id}>
            {nomeDi(u)}
          </option>
        ))}
      </select>
      {stato === "vietato" ? (
        <p className="text-xs text-ink/40">Non puoi assegnare record di questa organizzazione.</p>
      ) : stato === "pronto" && utenti.length === 0 ? (
        <p className="text-xs text-ink/40">
          Nessun utente assegnabile: serve una persona collegata a questa organizzazione.
        </p>
      ) : (
        aiuto && <p className="text-xs text-ink/40">{aiuto}</p>
      )}
    </div>
  );
}
